import React, { useState } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  X, 
  Eye,
  EyeOff,
  Star,
  MessageSquare
} from 'lucide-react';
import { useCMS } from '../../store/cmsStore';
import type { Testimonial } from '../../store/cmsStore';

export default function TestimonialEditor() {
  const { data, addTestimonial, updateTestimonial, deleteTestimonial } = useCMS();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<Omit<Testimonial, 'id'>>({
    name: '',
    text: '',
    rating: 5,
    date: new Date().toISOString().split('T')[0],
    active: true,
  });

  const handleEdit = (testimonial: Testimonial) => {
    setEditingId(testimonial.id);
    setFormData({
      name: testimonial.name,
      text: testimonial.text,
      rating: testimonial.rating,
      date: testimonial.date,
      active: testimonial.active,
    });
    setIsAdding(false);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    setFormData({
      name: '',
      text: '',
      rating: 5,
      date: new Date().toISOString().split('T')[0],
      active: true,
    });
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('يرجى إدخال اسم العميل');
      return;
    }
    if (!formData.text.trim()) {
      alert('يرجى إدخال نص التقييم');
      return;
    }
    if (editingId) {
      updateTestimonial(editingId, formData);
    } else {
      addTestimonial(formData);
    }
    setEditingId(null);
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
  };

  const handleDelete = (id: string) => {
    if (confirm('هل أنت متأكد من حذف هذا التقييم؟')) {
      deleteTestimonial(id);
    }
  };

  const toggleActive = (id: string, currentActive: boolean) => {
    updateTestimonial(id, { active: !currentActive });
  };

  const sortedTestimonials = [...data.testimonials].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 font-amiri">إدارة التقييمات</h2>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition font-cairo"
        >
          <Plus className="w-4 h-4" />
          إضافة تقييم جديد
        </button>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingId) && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-900 font-cairo">
              {editingId ? 'تعديل التقييم' : 'إضافة تقييم جديد'}
            </h3>
            <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                اسم العميل *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-cairo"
                placeholder="مثال: عبدالله محمد"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                التقييم
              </label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="focus:outline-none"
                  >
                    <Star
                      className={`w-8 h-8 transition ${
                        star <= formData.rating
                          ? 'text-yellow-400 fill-current'
                          : 'text-gray-300 hover:text-yellow-200'
                      }`}
                    />
                  </button>
                ))}
                <span className="mr-4 text-gray-600 font-cairo">{formData.rating} من 5</span>
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                التاريخ
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-cairo"
              />
            </div>

            {/* Text */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">
                نص التقييم *
              </label>
              <textarea
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent font-cairo"
                rows={4}
                placeholder="اكتب تجربة العميل..."
              />
            </div>

            {/* Active Toggle */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4 text-yellow-600 rounded focus:ring-yellow-500"
                />
                <span className="text-sm font-cairo text-gray-700">نشط</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-cairo transition"
            >
              إلغاء
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-cairo transition"
            >
              <Save className="w-4 h-4" />
              حفظ
            </button>
          </div>
        </div>
      )}

      {/* Testimonials List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">العميل</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">التقييم</th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 font-cairo">النص</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">التاريخ</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الحالة</th>
                <th className="text-center px-6 py-4 text-sm font-medium text-gray-500 font-cairo">الإجراءات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedTestimonials.map((testimonial) => (
                <tr key={testimonial.id} className="hover:bg-gray-50 transition">
                  <td className="px-6 py-4">
                    <div className="font-bold text-gray-900 font-cairo">{testimonial.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-0.5">
                      {Array.from({ length: testimonial.rating }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-gray-600 font-cairo text-sm line-clamp-2 max-w-xs">
                      {testimonial.text}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="text-gray-500 font-cairo text-sm">
                      {new Date(testimonial.date).toLocaleDateString('ar-SA')}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => toggleActive(testimonial.id, testimonial.active)}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-cairo transition ${
                        testimonial.active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {testimonial.active ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {testimonial.active ? 'نشط' : 'مخفي'}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handleEdit(testimonial)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                        title="تعديل"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(testimonial.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="حذف"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.testimonials.length === 0 && (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-cairo">لا توجد تقييمات بعد</p>
          </div>
        )}
      </div>
    </div>
  );
}